/**
 * Polymarket US inside the shared Game Detail panel (#987).
 *
 * Shows which account and market an order would use, why US is or is not
 * tradable right now, the US ladder for the selected team and side, and the
 * order-ticket slot. The ticket presenter (#988) mounts through `renderTicket`;
 * this host owns the account, market and outcome binding it receives. Saved
 * requests whose result is unconfirmed (a lost response) are listed for the
 * selected account under the account row, as on the standalone US page.
 *
 * Without a visible US account the market and ladder still show, read-only,
 * and the ticket slot says so: no order controls, recovery or price picking.
 */
import type { ReactNode } from 'react';
import { usEvidenceNow } from '@/lib/polymarketUs/timeEvidence';
import { cn } from '@/lib/infra/utils';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { useAccountStore } from '@/stores/accountStore';
import { ExchangeOrderBook, type SweepInfo } from '@/components/organisms/ExchangeOrderBook';
import { usBookForOutcome, usOutcomeFor } from '@/lib/polymarketUs/gameMapping';
import { usFullGameTotalFamily, usTotalOutcome } from '@/lib/polymarketUs/totalTarget';
import { usPeriodWinner } from '@/lib/polymarketUs/sports';
import { usGamePropFamily } from '@/lib/polymarketUs/gamePropFamily';
import { usStatLabel } from '@/lib/polymarketUs/statLabels';
import type { UsAccountContext } from '@/lib/polymarketUs/accountClient';
import type { UsTicketMount } from '@/views/PolymarketUs/UsPortfolioWorkspace';
import type { UsTradingClient } from '@/lib/polymarketUs/tradingClient';
import { UsRequestRecovery } from '@/views/PolymarketUs/UsRequestRecovery';
import { usAccountLabel, usReason } from '@/views/PolymarketUs/usVenueLabel';
import { usUnavailableLine } from '@/lib/polymarketUs/reason';
import type { UsGameVenueState } from './useUsGameVenue';
import type { UsLineTarget } from '@/lib/polymarketUs/gameMapping';
import type { UsHostMarketReadiness, UsRailControls } from '@/views/PolymarketUs/ticket/ConnectedUsTicket';
import type { UsDraftTerms } from '@/lib/polymarketUs/ticketModel';

const LINE_NAME: Record<UsLineTarget['kind'], string> = { moneyline: 'moneyline', spread: 'spread', total: 'full-game total', 'player-prop': 'player contract', 'game-prop': 'game contract', unsupported: 'market' };
const PER_LINE = new Set<UsLineTarget['kind']>(['spread', 'total']);

/** What a Game Detail ticket receives: the account mount plus the exact target. */
export type UsGameTicketMount = UsTicketMount & {
  railControls?: UsRailControls;
  target: { marketSlug: string; outcome: 'yes' | 'no'; label: string;
    /** #1030 — the venue's own name for `outcome` ("Over"/"Under") when the
     * host bound it by contract: the ticket refuses a market that names it otherwise. */
    nativeOutcomeLabel?: string };
  /** Price (cents) the trader picked from the US ladder, if any. */
  priceHintCents: number | null;
  /** Which pick `priceHintCents` is: a new value is a new command even at the same cents. */
  priceHintSeq: string | null;
  hostMarketReadiness: UsHostMarketReadiness;
  /** The host's memory of the trader's terms for this account and market (see UsManualTicket). */
  draftTerms: UsDraftTerms | null;
  onDraftTermsChange?(terms: UsDraftTerms): void;
  /** A cancel or submit is out (see UsManualTicket): the host holds its side, team and lane changes. */
  onCommittedChange?(committed: boolean): void;
};

const US_NO_ACCOUNT_TEXT = "Polymarket US trading isn't set up for you";
/** R4-F7: organization US keys reach a trader only in org-key mode; on personal keys the fix is a key-mode switch, not an account. */
const US_PERSONAL_KEYS_TEXT = 'You trade on personal keys; organization Polymarket US keys apply only in org-key mode. Ask a manager to switch you to org keys.';
/** #1114 T8: the freshness badge's quick reference, shown while it reads Live or Stale. */
const US_FRESHNESS_TITLE = "Live: this US book is inside its validity window. Stale: past it; its prices can't seed the ticket.";
// Echo only a well-formed account id: a link's text never becomes the message.
const ACCOUNT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** #1030 — why new orders wait while the shown total's catalogue refresh failed. */
export const US_LIST_REFRESHING = 'Polymarket US market list is refreshing; new orders are paused until it confirms this market.';

function readinessLine(context: UsAccountContext, currentBook: boolean, paused: string | null): string {
  if (context.readiness.state === 'ready') {
    return context.readiness.canSubmit
      ? paused ?? (currentBook ? 'Account enabled' : 'No current US book. New orders are disabled.')
      : 'View only: trading is not enabled for you on this account';
  }
  return `View only: ${usReason(context.readiness.reason)}`;
}

export interface UsVenueSectionProps {
  state: UsGameVenueState & { selectAccount(accountId: string): void };
  /** The board line this section stands for (default moneyline). */
  lineKind?: UsLineTarget['kind'];
  /** #1030 — a total's displayed side. Totals have no YES team: Over YES is
   * native YES, Under YES is native NO. Required for `lineKind: 'total'`. */
  totalSide?: 'over' | 'under';
  teamCode: string;
  teamName: string;
  /** #1247: the other team's code, for the moneyline caption "(GSW wins)" on a NO. */
  opponentCode?: string;
  boardSide: 'yes' | 'no';
  onBoardSideChange?(side: 'yes' | 'no'): void;
  startTimeMs?: number | null;
  maxLevels: number;
  priceHintCents: number | null;
  priceHintSeq?: string | null;
  draftTerms?: UsDraftTerms | null;
  onDraftTermsChange?(terms: UsDraftTerms): void;
  onCommittedChange?(committed: boolean): void;
  onLevelClick(info: SweepInfo): void;
  onRequestRecorded(): void;
  renderTicket?: (mount: UsGameTicketMount) => ReactNode;
  /** Reads locally saved requests for the selected account; never resubmits. */
  recovery?: Pick<UsTradingClient, 'setScope' | 'pending' | 'recover' | 'acknowledge'>;
  /** Bumped when the ticket records a request, so the list re-reads. */
  requestRevision?: number;
}

export function UsVenueSection({
  state, lineKind = 'moneyline', totalSide, teamCode, teamName, opponentCode, boardSide, onBoardSideChange, startTimeMs = null, maxLevels, priceHintCents, priceHintSeq = null, draftTerms = null, onDraftTermsChange, onCommittedChange, onLevelClick, onRequestRecorded, renderTicket,
  recovery, requestRevision = 0,
}: UsVenueSectionProps) {
  const now = useSharedNow(1000, state.status === 'ready' && state.book?.state === 'current');
  const personalKeys = useAccountStore((s) => s.keyModeInfo !== null && !s.keyModeInfo.useOrgKeys);
  const noAccountText = personalKeys ? US_PERSONAL_KEYS_TEXT : US_NO_ACCOUNT_TEXT;
  if (state.status === 'off') return null;
  if (state.status === 'loading') {
    return <p className="text-muted-foreground p-3 text-xs" data-testid="us-venue-status">Loading Polymarket US…</p>;
  }
  const currentBook = state.status === 'ready' && state.book?.state === 'current'
    && state.book.bookExpiresAtMs !== null && state.book.bookExpiresAtMs > usEvidenceNow(state.book.timeEvidence, now);
  // #1030 — the market on screen is from the last complete catalogue read; the
  // latest refresh could not confirm it. Its ticket stays, orders wait.
  const paused = state.status === 'ready' && state.catalogueUnconfirmed ? US_LIST_REFRESHING : null;
  const noAccountRow = (
    <p className="text-muted-foreground text-[11px] [overflow-wrap:anywhere]" data-testid="us-venue-no-account">
      {noAccountText}
    </p>
  );
  if (state.status === 'error') {
    return (
      <div className="space-y-1 p-3" data-testid="us-venue-status">
        {state.noAccount && noAccountRow}
        <p className="text-xs text-amber-400 [overflow-wrap:anywhere]">{usUnavailableLine(state.code)}</p>
      </div>
    );
  }
  if (state.status === 'account-unavailable') {
    // #1006 — the caller named an exact account; no other is chosen for the trader.
    return (
      <div className="space-y-1 p-3" data-testid="us-venue-status">
        <p className="text-xs text-amber-400 light:text-amber-700 [overflow-wrap:anywhere]" data-testid="us-venue-account-unavailable">
          {`${ACCOUNT_ID.test(state.requestedAccountId) ? `Account ${state.requestedAccountId}` : 'The requested account'} isn't among the Polymarket US accounts this panel can use.`}
        </p>
        {state.accounts.length > 0 ? (
          <select
            aria-label="Polymarket US account"
            // Options carry the full id: never wider than the rail.
            className="border-border bg-background w-full max-w-full rounded border px-1 py-0.5 text-[11px]"
            value=""
            onChange={(e) => state.selectAccount(e.target.value)}
          >
            <option value="" disabled>Choose an account</option>
            {state.accounts.map((a, i) => (
              <option key={a.accountId} value={a.accountId}>
                {`${usAccountLabel(a.ownership, i, state.accounts.length)} · ${a.accountId}`}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-muted-foreground text-[11px]">{personalKeys ? noAccountText : `${noAccountText}.`}</p>
        )}
      </div>
    );
  }
  // #1238 — the account's id rides in its label's hover, never as a line.
  const accountTitle = state.noAccount ? undefined
    : `${usAccountLabel(state.context.ownership,
      Math.max(0, state.accounts.findIndex((a) => a.accountId === state.context.accountId)),
      Math.max(1, state.accounts.length))} · account ${state.context.accountId}`;
  const accountRow = state.noAccount ? noAccountRow : (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]" data-testid="us-venue-account">
      {/* #1238 — the numbered label names the account; the exact id is in its
          hover, as the Tracker's account word carries it (#1006). */}
      {state.accounts.length > 1 ? (
        <select
          aria-label="Polymarket US account"
          className="border-border bg-background rounded border px-1 py-0.5"
          value={state.accountId}
          onChange={(e) => state.selectAccount(e.target.value)}
          data-testid="us-venue-account-id"
          data-account-id={state.context.accountId}
          title={accountTitle}
        >
          {state.accounts.map((a, i) => (
            <option key={a.accountId} value={a.accountId}>
              {usAccountLabel(a.ownership, i, state.accounts.length)}
            </option>
          ))}
        </select>
      ) : (
        <span
          className="text-muted-foreground"
          data-testid="us-venue-account-id"
          data-account-id={state.context.accountId}
          title={accountTitle}
        >
          {usAccountLabel(state.context.ownership, 0, 1)}
        </span>
      )}
      <span className={cn('min-w-0 [overflow-wrap:anywhere]', state.context.readiness.state === 'ready'
        && state.context.readiness.canSubmit && currentBook && !paused
        ? 'text-emerald-400 light:text-emerald-700' : 'text-amber-400 light:text-amber-700')}
      >
        {readinessLine(state.context, currentBook, paused)}
      </span>
    </div>
  );
  // #1247: in the ticket's Order Entry row the account is a compact chip —
  // provenance, never a gate (the ticket states every account guard itself).
  // Named as everywhere (#1238): the label, the exact id in its title and
  // `data-account-id`; the chip's own title adds the readiness the hidden
  // account row would have shown. Several accounts keep the picker, at chip size.
  const accountChip = state.noAccount ? null : (
    <span data-testid="us-venue-account" title={`${accountTitle} · ${readinessLine(state.context, currentBook, paused)}`}
      className="flex min-w-0 items-center">
      {state.accounts.length > 1 ? (
        <select
          aria-label="Polymarket US account"
          className="border-border bg-background text-muted-foreground min-w-0 max-w-[9rem] truncate rounded border px-1 py-0 text-[10px]"
          value={state.accountId}
          onChange={(e) => state.selectAccount(e.target.value)}
          data-testid="us-venue-account-id"
          data-account-id={state.context.accountId}
          title={accountTitle}
        >
          {state.accounts.map((a, i) => (
            <option key={a.accountId} value={a.accountId}>
              {usAccountLabel(a.ownership, i, state.accounts.length)}
            </option>
          ))}
        </select>
      ) : (
        <span className="bg-muted text-muted-foreground min-w-0 truncate rounded px-1.5 py-px text-[10px] font-medium"
          data-testid="us-venue-account-id" data-account-id={state.context.accountId} title={accountTitle}>
          {usAccountLabel(state.context.ownership, 0, 1)}
        </span>
      )}
      {/* #1006/#1247 — the label and exact id read aloud (a title is not reliably announced). */}
      <span className="sr-only">{accountTitle}</span>
    </span>
  );
  const recoveryList = recovery && !state.noAccount ? (
    <div className="px-3" data-testid="us-venue-recovery">
      <UsRequestRecovery
        key={`${state.context.accountId}|${state.context.scope.revision}`}
        client={recovery}
        accountId={state.context.accountId}
        scope={state.context.scope}
        refreshRevision={requestRevision}
      />
    </div>
  ) : null;
  if (state.status === 'no-market') {
    return (
      <div className="space-y-1 p-3" data-testid="us-venue-status">
        {accountRow}
        <p className="text-muted-foreground text-xs" data-testid={state.missingMarketSlug ? 'us-venue-market-unlisted' : undefined}>
          {state.missingMarketSlug
            // True for an order or a holding, and whether the market went unlisted
            // or is listed in a shape the tab cannot join as this game's moneyline.
            ? `The US tab can't open this row's market (${state.missingMarketSlug}) as this game's ${lineKind === 'total' ? 'full-game total' : 'moneyline'} right now; no other market is used in its place.`
            : lineKind === 'unsupported'
            ? 'Polymarket US lists no market of this kind.'
            // #1238 — the K/P words ("No Kalshi market available for this team.").
            : `No Polymarket US market available for this ${lineKind === 'moneyline' ? 'team' : PER_LINE.has(lineKind) ? 'line' : 'game'}.`}
        </p>
        {recoveryList}
      </div>
    );
  }
  if (state.status === 'ambiguous') {
    return (
      <div className="space-y-1 p-3" data-testid="us-venue-status">
        {accountRow}
        <p className="text-xs text-amber-400">
          {`Polymarket US lists ${state.count} ${LINE_NAME[lineKind]}s for this ${PER_LINE.has(lineKind) ? 'line' : 'game'}; none is shown until the market is unambiguous.`}
        </p>
        {recoveryList}
      </div>
    );
  }

  const { market, book, bookError } = state;
  const period = usPeriodWinner(market.sportsMarketType, market.sport);
  if (period && market.yesTeam !== teamCode) {
    return <p className="p-3 text-xs text-muted-foreground" data-testid="us-venue-status">
      No Polymarket US contract for the selected team and period.
    </p>;
  }
  // #1030 — a total binds by its native family and side, never a YES team.
  const totalFamily = lineKind === 'total' ? usFullGameTotalFamily(market.sportsMarketType, market.sport) : null;
  // #1110: a binary game prop carries the venue's 0.5 as evidence, not a line to show.
  const propLine = lineKind === 'game-prop' && usGamePropFamily(market.sportsMarketType, market.sport)?.kind === 'binary' ? null : market.line;
  if (lineKind === 'total' && (!totalFamily || !totalSide || market.line === null || market.yesTeam !== null)) {
    return <p className="p-3 text-xs text-muted-foreground" data-testid="us-venue-status">
      No Polymarket US contract for the selected total side.
    </p>;
  }
  const hostMarketReadiness: UsHostMarketReadiness = paused ? { state: 'unavailable', reason: paused }
    : currentBook && book && book.bookExpiresAtMs !== null
      ? { state: 'current', expiresAtMs: book.bookExpiresAtMs, timeEvidence: book.timeEvidence }
      : { state: 'unavailable', reason: 'No current US book. New orders are disabled.' };
  const yesTeam = market.yesTeam ?? '';
  const outcome = lineKind === 'player-prop' || lineKind === 'game-prop' ? boardSide
    : totalFamily && totalSide ? usTotalOutcome(totalSide, boardSide) : usOutcomeFor(teamCode, yesTeam, boardSide);
  const ladder = book ? usBookForOutcome(book, outcome) : null;
  // A total's side and threshold come from the native market, not the board's name.
  const sideName = totalFamily && totalSide
    ? `${totalSide === 'over' ? totalFamily.yesLabel : totalFamily.noLabel} ${market.line}` : teamName;
  const target = {
    marketSlug: market.marketSlug,
    outcome,
    label: `${sideName} ${boardSide === 'yes' ? 'YES' : 'NO'} (Polymarket US ${outcome.toUpperCase()})`,
    ...(totalFamily ? { nativeOutcomeLabel: outcome === 'yes' ? totalFamily.yesLabel : totalFamily.noLabel } : {}),
  };
  const freshness = book
    ? currentBook ? 'Live' : book.state === 'unavailable' ? 'No quote' : 'Stale'
    : bookError ? 'Unavailable' : 'Loading';
  const binding = `${totalFamily ? `Full-game total · YES = ${totalFamily.yesLabel} ${market.line} (combined ${totalFamily.unit}) · NO = ${totalFamily.noLabel} ${market.line}` : lineKind === 'game-prop' ? `${market.title ?? 'US game contract'} · Full game${propLine === null ? '' : ` · Line ${propLine}`}` : lineKind === 'player-prop' ? `${market.title ?? `${market.playerProp?.player} · ${usStatLabel(market.playerProp?.stat ?? '')}`} · Full game${propLine === null ? '' : ` · Line ${propLine}`}` : lineKind === 'spread' ? `Spread · YES = ${yesTeam} ${market.line ?? ''}` : `${period ? `${period} winner` : 'Moneyline'} · YES = ${yesTeam}`}`;
  // #1247, as K/P's caption under BUY YES / BUY NO: what the selected side backs.
  const backs = lineKind === 'player-prop' || lineKind === 'game-prop'
    // A contract's own title names its line; an untitled player contract adds it, as its binding does.
    ? `${boardSide.toUpperCase()} · ${market.title ?? (lineKind === 'player-prop'
      ? `${market.playerProp?.player} · ${usStatLabel(market.playerProp?.stat ?? '')}${propLine === null ? '' : ` ${propLine}`}` : 'US game contract')}`
    : totalFamily ? `(${outcome === 'yes' ? totalFamily.yesLabel : totalFamily.noLabel} ${market.line})`
      : lineKind === 'spread' ? `(${boardSide === 'yes' ? teamName : teamName.includes(' wins by over ') ? teamName.replace(' wins by over ', ' does not win by over ') : `not ${teamName}`})`
        : boardSide === 'yes' ? `(${teamCode} wins${period ? ` ${period}` : ''})`
          : opponentCode ? `(${opponentCode} wins${period ? ` ${period}` : ''})` : `(${teamCode} loses${period ? ` ${period}` : ''})`;
  const sideCaption = { text: backs, title: `${binding} · this ticket buys/sells US ${outcome.toUpperCase()}` };
  // As the Kalshi ticket's "Price (¢) · POR": the team the market is quoted for.
  const priceLabelSuffix = lineKind === 'moneyline' || lineKind === 'spread' ? teamCode || undefined : undefined;
  return (
    <div className="space-y-2" data-testid="us-venue-section">
      {/* Read-only (no account): the binding is said here, where no ticket stands.
          With the rail ticket, the account is the chip in its Order Entry row
          and the binding is the title of its side caption (#1247). A host
          without that ticket keeps the account row and the binding line. */}
      {(state.noAccount || !renderTicket || !onBoardSideChange) && (
        <div className="space-y-1 px-3 pt-3">
          {!state.noAccount && accountRow}
          <p className="text-muted-foreground text-[11px]" data-testid="us-venue-target">
            {`${binding} · ${state.noAccount ? `showing US ${outcome.toUpperCase()}` : `this ticket buys/sells US ${outcome.toUpperCase()}`}`}
          </p>
        </div>
      )}
      {recoveryList}
      <div className={cn('px-3', !state.noAccount && renderTicket && onBoardSideChange && 'pt-3')} data-testid="us-venue-ticket">
        {state.noAccount ? (
          // In place of the ticket: display only, no order controls.
          <p className="text-muted-foreground border-border rounded border border-dashed p-2 text-xs" data-testid="us-venue-no-account">
            {`${personalKeys ? noAccountText : `${noAccountText}.`} Prices are shown read-only.`}
          </p>
        ) : renderTicket ? (<>
          {/* Read aloud: the side caption's title is not reliably announced. */}
          {onBoardSideChange && <p className="sr-only" data-testid="us-venue-target">{sideCaption.title}</p>}
          {renderTicket({ context: state.context, onRequestRecorded, target, priceHintCents, priceHintSeq, hostMarketReadiness, draftTerms, onDraftTermsChange, onCommittedChange,
            railControls: onBoardSideChange ? { side: boardSide, onSideChange: onBoardSideChange, startTimeMs, sideCaption, priceLabelSuffix, accountChip } : undefined })}
        </>) : (
          <p className="text-muted-foreground border-border rounded border border-dashed p-2 text-xs">
            Polymarket US order entry is not available in this build.
          </p>
        )}
      </div>
      <div className="px-3 pb-3">
        {bookError && !book && <p className="mb-1 text-xs text-amber-400 [overflow-wrap:anywhere]">{usReason(bookError)}</p>}
        <ExchangeOrderBook
          venue="polymarket_us"
          maxLevels={maxLevels}
          teamLabel={`${sideName} — ${boardSide === 'yes' ? 'YES' : 'NO'}`}
          headerRight={
            <span
              className={freshness === 'Live' ? 'text-[10px] text-emerald-400' : 'text-[10px] text-amber-400'}
              data-testid="us-book-freshness"
              title={freshness === 'Live' || freshness === 'Stale' ? US_FRESHNESS_TITLE : undefined}
            >
              {freshness}
            </span>
          }
          bids={ladder?.bids ?? []}
          asks={ladder?.asks ?? []}
          // A picked price only seeds the ticket; there is none without an account.
          onLevelClick={state.noAccount || !currentBook ? undefined : onLevelClick}
        />
        {ladder?.partial && (
          <p className="text-muted-foreground mt-1 text-[10px]">Depth beyond the levels shown is not included.</p>
        )}
      </div>
    </div>
  );
}
